// ---------------------------------------------------------------------------
// PleasantPlusPaywall — the "fifth drive" moment, in Pleasant's voice.
// Four drives were on us; this asks for $1.99/month to keep wandering.
// App Review requirements honored: clear price + renewal language, a
// Restore Purchases button, and Terms / Privacy links.
// ---------------------------------------------------------------------------
import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { purchasePlus, restorePlus } from "@/lib/pleasantPlus";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called when the entitlement became active (purchase or restore). */
  onSubscribed: () => void;
}

export const PleasantPlusPaywall = ({ open, onClose, onSubscribed }: Props) => {
  const [busy, setBusy] = useState<"buy" | "restore" | null>(null);
  if (!open) return null;

  const buy = async () => {
    setBusy("buy");
    const ok = await purchasePlus();
    setBusy(null);
    if (ok) {
      toast.success("Welcome to Pleasant Monthly — enjoy the drive.");
      onSubscribed();
    } else {
      toast.info("No charge was made. You can subscribe any time.");
    }
  };

  const restore = async () => {
    setBusy("restore");
    const ok = await restorePlus();
    setBusy(null);
    if (ok) {
      toast.success("Subscription restored.");
      onSubscribed();
    } else {
      toast.info("No previous subscription found for this Apple ID.");
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-3xl bg-card border border-border shadow-elevated p-6 pb-8">
        <div className="flex items-center gap-2 text-primary mb-3">
          <Sparkles className="w-5 h-5" />
          <span className="wordmark text-3xl leading-none">Pleasant</span>
        </div>
        <h2 className="text-lg font-semibold mb-1">Your first four drives were on us</h2>
        <p className="text-sm text-muted-foreground mb-5">
          Keep taking the pleasant way — every drive, walk, and ride, on your
          phone and in the car — for <span className="font-semibold text-foreground">$1.99 a month</span>.
        </p>
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => void buy()}
          className="w-full h-12 rounded-full bg-pleasant text-primary-foreground font-semibold shadow-glow flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {busy === "buy" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Continue — $1.99/month
        </button>
        <p className="text-[11px] text-muted-foreground text-center mt-3">
          Auto-renews monthly until cancelled in Settings → Apple ID → Subscriptions.
        </p>
        <div className="flex items-center justify-center gap-4 mt-4 text-xs">
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => void restore()}
            className="text-primary font-medium hover:underline disabled:opacity-60"
          >
            {busy === "restore" ? "Restoring…" : "Restore Purchases"}
          </button>
          <a className="text-muted-foreground hover:underline" href="https://pleasantroutes.com/terms" target="_blank" rel="noreferrer">Terms</a>
          <a className="text-muted-foreground hover:underline" href="https://pleasantroutes.com/privacy" target="_blank" rel="noreferrer">Privacy</a>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-full text-center text-xs text-muted-foreground mt-4 hover:underline"
        >
          Not now
        </button>
      </div>
    </div>
  );
};
